/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/web-components/autocomplete-input/w-autocomplete-input.css" />
 */

import template from "./template.js";

class AutocompleteInput extends HTMLElement {
  constructor() {
    super();
    this.selectedOptions = [];
    this.customOptions = []; // Store custom options added at runtime
  }

  connectedCallback() {
    const id = this.getAttribute("id");
    const name = this.getAttribute("name");
    const dataValidate = this.getAttribute("data-validate");
    const dataMarkError = this.getAttribute("data-mark-error");
    const dataInlineError = this.getAttribute("data-inline-error");
    const enableCustom = this.getAttribute("enable-custom") === "true";

    const initialOptionsString = this.getAttribute("options");
    const initialOptions = initialOptionsString
      ? initialOptionsString.split(",")
      : [];

    this.innerHTML = template({});

    const container = this.querySelector(".autocomplete-input-container");
    const inputWrapper = this.querySelector(".autocomplete-input");
    const optionElementsWrapper = this.querySelector(
      ".autocomplete-input-options"
    );
    const hiddenSelect = this.querySelector("select");
    const inputElement = this.querySelector("input");
    const parentId = this.getAttribute("data-parent");
    const parent = document.querySelector(parentId) || document.body;

    if (dataValidate) {
      hiddenSelect.setAttribute("data-validate", dataValidate);
    }
    if (dataMarkError) {
      hiddenSelect.setAttribute("data-mark-error", dataMarkError);
    }
    if (dataInlineError) {
      hiddenSelect.setAttribute("data-inline-error", dataInlineError);
    }

    // Helper function to get all available options (initial + custom)
    const getAllOptions = () => {
      return [...initialOptions, ...this.customOptions];
    };

    const removeSelectedOption = (e) => {
      const wrapper = e.target.parentNode;
      const wrapperInnerText = wrapper.innerText;
      const selectedOption = this.querySelector(
        `[option-name="${wrapperInnerText}"]`
      );
      if (selectedOption) {
        selectedOption.classList.remove("selected");
      }
      wrapper.remove();

      // Remove selected in hidden select
      const hiddenOption = hiddenSelect.querySelector(
        `[value="${wrapperInnerText}"]`
      );
      if (hiddenOption) {
        hiddenOption.removeAttribute("selected");
      }

      this.selectedOptions = this.selectedOptions.filter(
        (selectedOption) => selectedOption !== wrapperInnerText
      );
      e.stopPropagation();
    };

    // Function that create a option chip inside of a wrapper with the given value
    const createSelectedOptionChip = (wrapper, value) => {
      const optionSpan = document.createElement("span");
      optionSpan.classList.add("autocomplete-selected-option");
      optionSpan.innerText = value;

      const close = document.createElement("i");
      close.classList.add("icon-remove");
      close.addEventListener("click", removeSelectedOption);

      optionSpan.appendChild(close);

      wrapper.insertBefore(
        optionSpan,
        wrapper.querySelector(".is-flex-grow-1")
      );
    };

    // Function that render a options
    const createOptionsElements = (optionList) => {
      const optionsHtml = [];
      optionList.forEach((option) => {
        const optionElement = document.createElement("p");
        optionElement.classList.add("autocomplete-option");
        if (this.selectedOptions.includes(option)) {
          optionElement.classList.add("selected");
        }
        optionElement.setAttribute("option-name", option);
        optionElement.innerText = option;
        optionElement.addEventListener("click", () => {
          inputElement.value = "";

          // Set selected true in  hidden select
          const hiddenOption = hiddenSelect.querySelector(
            `[value="${option}"]`
          );
          hiddenOption.setAttribute("selected", true);

          this.selectedOptions.push(option);
          optionElement.classList.add("selected");
          createSelectedOptionChip(inputWrapper, option);
        });
        optionsHtml.push(optionElement);
      });
      return optionsHtml;
    };

    const createHiddenOptions = (optionList) => {
      const hiddenOptions = [];
      hiddenSelect.setAttribute("name", name);
      optionList.forEach((option) => {
        // Check if option already exists in hidden select (to preserve selected state)
        let optionElement = hiddenSelect.querySelector(`[value="${option}"]`);
        if (!optionElement) {
          optionElement = document.createElement("option");
          optionElement.setAttribute("value", option);
          optionElement.innerText = option;
        }
        // Preserve selected state if it was already selected
        if (this.selectedOptions.includes(option)) {
          optionElement.setAttribute("selected", true);
        }
        hiddenOptions.push(optionElement);
      });
      return hiddenOptions;
    };

    const clickOnWrapper = () => {
      inputElement.focus();
      // Use CSS class for outline instead of inline style for CSP compliance
      inputWrapper.classList.add("outline-accent");
      optionElementsWrapper.classList.remove("is-hidden");
      const { bottom } = utils.getPopoverPositions(
        inputWrapper,
        optionElementsWrapper,
        parent
      );
      if (bottom === 0) {
        optionElementsWrapper.classList.add("on-top");
      }
    };

    const renderOptions = (optionList) => {
      optionElementsWrapper.innerHTML = "";
      const optionElements = createOptionsElements(optionList);
      optionElements.forEach((el) => {
        optionElementsWrapper.appendChild(el);
      });

      const optionElement = document.createElement("p");
      optionElement.classList.add("autocomplete-option");
      optionElement.classList.add("no-data-found");
      optionElement.innerText = "No data found";
      optionElementsWrapper.appendChild(optionElement);

      // Store currently selected values before clearing
      const currentlySelected = Array.from(
        hiddenSelect.querySelectorAll("option[selected]")
      ).map((opt) => opt.value);

      hiddenSelect.innerHTML = "";
      const hiddenOptions = createHiddenOptions(optionList);
      hiddenOptions.forEach((el) => {
        hiddenSelect.appendChild(el);
      });

      // Restore selected state for options that were previously selected
      currentlySelected.forEach((selectedValue) => {
        const option = hiddenSelect.querySelector(`[value="${selectedValue}"]`);
        if (option) {
          option.setAttribute("selected", true);
        }
      });
    };

    if (id) {
      this.removeAttribute("id");
      container.setAttribute("id", id);
    }
    container.addEventListener("click", clickOnWrapper);
    renderOptions(getAllOptions());

    const filterOptions = (optionList = []) => {
      const optionElements = this.querySelectorAll(".autocomplete-option");
      optionElements.forEach((el) => {
        if (el.classList.contains("no-data-found")) {
          return; // Skip the "no data found" element
        }
        if (!optionList.includes(el.innerText)) {
          el.classList.add("is-hidden");
        } else {
          el.classList.remove("is-hidden");
        }
      });

      const noDataFound = this.querySelector(
        ".autocomplete-option.no-data-found"
      );
      if (optionList.length === 0) {
        if (enableCustom) {
          // If custom input is enabled, only show message when user has typed something
          if (inputElement.value.trim()) {
            noDataFound.innerText = "Press Enter to add as tag";
            noDataFound.classList.remove("is-hidden");
          } else {
            // Hide "No data found" when custom is enabled and input is empty
            noDataFound.classList.add("is-hidden");
          }
        } else {
          // Show "No data found" only when custom input is not enabled
          noDataFound.innerText = "No data found";
          noDataFound.classList.remove("is-hidden");
        }
      } else {
        noDataFound.classList.add("is-hidden");
      }
    };

    const handleInputElement = (e) => {
      const allOptions = getAllOptions();
      if (!e.target.value) {
        filterOptions(allOptions);
      } else {
        const optionList = allOptions.filter((option) =>
          option.toLowerCase().includes(e.target.value.toLowerCase())
        );
        filterOptions(optionList);
      }
    };

    // Function to add a custom tag
    const addCustomTag = (value) => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return false;
      }

      // Check if already selected
      if (this.selectedOptions.includes(trimmedValue)) {
        return false;
      }

      // Check if it's already in initial options
      if (initialOptions.includes(trimmedValue)) {
        // If it exists in initial options, just select it normally
        const optionElement = this.querySelector(
          `[option-name="${trimmedValue}"]`
        );
        if (optionElement && !optionElement.classList.contains("selected")) {
          optionElement.click();
        }
        return true;
      }

      // Add to custom options if not already there
      if (!this.customOptions.includes(trimmedValue)) {
        this.customOptions.push(trimmedValue);
      }

      // Create hidden option if it doesn't exist
      let hiddenOption = hiddenSelect.querySelector(
        `[value="${trimmedValue}"]`
      );
      if (!hiddenOption) {
        hiddenOption = document.createElement("option");
        hiddenOption.setAttribute("value", trimmedValue);
        hiddenOption.innerText = trimmedValue;
        hiddenSelect.appendChild(hiddenOption);
      }
      hiddenOption.setAttribute("selected", true);

      // Add to selected options
      this.selectedOptions.push(trimmedValue);

      // Create the chip
      createSelectedOptionChip(inputWrapper, trimmedValue);

      // Re-render options to include the new custom option
      renderOptions(getAllOptions());

      return true;
    };

    // Handle Enter key to add custom tags
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && enableCustom) {
        e.preventDefault();
        const inputValue = inputElement.value;
        if (inputValue) {
          if (addCustomTag(inputValue)) {
            inputElement.value = "";
            // Hide the options dropdown
            optionElementsWrapper.classList.add("is-hidden");
          }
        }
      }
    };

    inputElement.addEventListener("input", handleInputElement);
    inputElement.addEventListener("focus", handleInputElement);
    inputElement.addEventListener("keydown", handleKeyDown);

    // Dismiss on outside click
    document.addEventListener("click", (e) => {
      const isClickInside = container.contains(e.target);

      if (!isClickInside) {
        optionElementsWrapper.classList.add("is-hidden");
        // Remove outline class for CSP compliance
        inputWrapper.classList.remove("outline-accent");
      }
    });
  }
}

window.customElements.define("w-autocomplete-input", AutocompleteInput);
